import { Button, ButtonGroupRow, ButtonPopup, LoadingIndicator, TextHeading } from '@isdd/idsk-ui-kit/index'
import {
    ApiStandardRequestPreviewRequestChannel,
    useActionStandardRequest,
    useAssignStandardRequest,
} from '@isdd/metais-common/api/generated/standards-swagger'
import styles from '@isdd/metais-common/components/entity-header/ciEntityHeader.module.scss'
import { useActionSuccess } from '@isdd/metais-common/contexts/actionSuccess/actionSuccessContext'
import { useInvalidateCodeListCache, useInvalidateStandardListRequests } from '@isdd/metais-common/hooks/invalidate-cache'
import { Can, useAbilityContextWithFeedback } from '@isdd/metais-common/hooks/permissions/useAbilityContext'
import { Actions } from '@isdd/metais-common/hooks/permissions/useUserAbility'
import { useScroll } from '@isdd/metais-common/hooks/useScroll'
import { MutationFeedback } from '@isdd/metais-common/index'
import { NavigationSubRoutes } from '@isdd/metais-common/navigation/routeNames'
import { StandardDraftsDraftStates } from '@isdd/metais-common/types/api'
import { getErrorTranslateKey } from '@isdd/metais-common/utils/errorMapper'
import React, { useCallback, useEffect, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { transformTargetStateIntoAction } from '@/componentHelpers/draftsList'
import { DraftsListAssignToPSDialog } from '@/components/entities/draftslist/DraftsListAssignToPSDialog'
import { DraftsListButtonPopupContent } from '@/components/entities/draftslist/DraftsListButtonPopupContent'
import { DraftsListChangeStateModal } from '@/components/entities/draftslist/DraftsListChangeStateModal'
import { StandardDraftsEvent } from '@isdd/metais-common/components/state-machine/standardDraftsListStateMachine'

interface Props {
    entityId: string
    entityItemName: string
    requestChannel: string
}

export const DraftsListIdHeader: React.FC<Props> = ({ entityId, entityItemName, requestChannel }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const { invalidateAll: invalidateCodelists } = useInvalidateCodeListCache()
    const { invalidate } = useInvalidateStandardListRequests(parseInt(entityId ?? ''))
    const { isActionSuccess } = useActionSuccess()
    const { wrapperRef, scrollToMutationFeedback } = useScroll()
    const [openChangeStateDialog, setOpenChangeStateDialog] = useState<boolean>(false)
    const [targetState, setTargetState] = useState<StandardDraftsEvent['type']>()
    const { ability } = useAbilityContextWithFeedback()
    const {
        mutateAsync: changeState,
        isSuccess: mutationIsSuccess,
        isError: mutationIsError,
        error: error,
        isPending: mutationIsLoading,
    } = useActionStandardRequest()

    const errorKey = getErrorTranslateKey(error)
    const { mutateAsync: assignToPS, isPending, isSuccess, isError } = useAssignStandardRequest()

    const onClick = (incomingState: StandardDraftsEvent['type']) => {
        if (incomingState !== StandardDraftsDraftStates.ASSIGNED) setOpenChangeStateDialog(true)
        setTargetState(incomingState)
    }

    const closeDialog = () => {
        setTargetState(undefined)
    }

    const handleChangeState = useCallback(
        async (description: string) => {
            setOpenChangeStateDialog(false)
            await changeState({
                standardRequestId: parseInt(entityId ?? ''),
                data: {
                    description: description,
                },
                params: {
                    action: transformTargetStateIntoAction(targetState as StandardDraftsDraftStates),
                },
            })

            if (
                requestChannel === ApiStandardRequestPreviewRequestChannel.ZC_Header ||
                requestChannel === ApiStandardRequestPreviewRequestChannel.ZC_Request
            ) {
                invalidateCodelists()
            }
            invalidate()
        },
        [changeState, entityId, invalidateCodelists, invalidate, requestChannel, targetState],
    )
    const handleAssignToPS = useCallback(
        async (values: FieldValues) => {
            closeDialog()
            await assignToPS({
                groupId: values?.workGroupId,
                standardRequestId: parseInt(entityId),
            })
        },
        [assignToPS, entityId],
    )

    useEffect(() => {
        scrollToMutationFeedback()
    }, [isActionSuccess, scrollToMutationFeedback])

    const buttonPopupContent = DraftsListButtonPopupContent({ onClick })

    const showMoreActionButton = buttonPopupContent.allPosibleSteps?.find((step) => ability.can(Actions.CREATE, `DraftsList.${step}`))

    return (
        <>
            <MutationFeedback
                success={mutationIsSuccess || isSuccess || isActionSuccess.value}
                error={mutationIsError || isError}
                errorMessage={errorKey ? t(errorKey) : undefined}
                successMessage={isActionSuccess?.additionalInfo?.type == 'create' ? t('mutationFeedback.successfulCreated') : undefined}
            />

            <div ref={wrapperRef} />
            {(mutationIsLoading || isPending) && <LoadingIndicator label={t('feedback.saving')} />}
            <div className={styles.headerDiv}>
                <DraftsListChangeStateModal
                    openChangeStateDialog={openChangeStateDialog}
                    setOpenChangeStateDialog={setOpenChangeStateDialog}
                    handleChangeState={handleChangeState}
                    targetState={targetState}
                />
                <DraftsListAssignToPSDialog
                    openAddToPSDialog={targetState === StandardDraftsDraftStates.ASSIGNED}
                    setOpenAddToPSDialog={closeDialog}
                    assignToPs={handleAssignToPS}
                />
                <TextHeading size="XL">{entityItemName}</TextHeading>
                <ButtonGroupRow>
                    <Can I={Actions.EDIT} a={`DraftsList`}>
                        <Button
                            label={t('ciType.editButton')}
                            onClick={() => navigate(`${NavigationSubRoutes.ZOZNAM_NAVRHOV}/${entityId}/edit`, { state: location.state })}
                        />
                    </Can>
                    {showMoreActionButton && (
                        <Can I={Actions.CHANGE_STATES} a={`DraftsList`}>
                            <ButtonPopup
                                buttonLabel={t('ciType.moreButton')}
                                popupHorizontalPosition="right"
                                popupContent={() => {
                                    return <div className={styles.buttonLinksDiv}>{buttonPopupContent.component}</div>
                                }}
                            />
                        </Can>
                    )}
                </ButtonGroupRow>
            </div>
        </>
    )
}
